import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, ExternalLink, Home, ShoppingCart, MessageCircle, Lock, Shield } from 'lucide-react';

/**
 * AccessDeniedPopup - Clean Professional Design
 * Theme: Matches Landing Page Blue (bg-blue-600 = #2563EB)
 */
const AccessDeniedPopup = ({ 
  isOpen, 
  reason = 'not_registered',
  onGoToLanding
}) => {
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowContent(false);
      const timer = setTimeout(() => setShowContent(true), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const getReasonContent = () => {
    switch (reason) {
      case 'not_registered':
        return { 
          title: 'Access Denied', 
          subtitle: 'Account Not Registered', 
          message: 'Your account is not in our system. Contact us to get access!',
          icon: Shield
        };
      case 'no_permission':
        return { 
          title: 'Hold On!', 
          subtitle: 'Insufficient Permissions', 
          message: 'You don\'t have permission for this page. Need access? Reach out!',
          icon: Lock
        };
      case 'unauthorized':
        return { 
          title: 'Access Blocked', 
          subtitle: 'Unauthorized Attempt', 
          message: 'This action isn\'t allowed. Let\'s get you sorted!',
          icon: Shield
        };
      default:
        return { 
          title: 'Access Denied', 
          subtitle: 'Permission Required', 
          message: 'Your account needs additional permissions.',
          icon: Lock
        };
    }
  };

  const content = getReasonContent();
  const IconComponent = content.icon;

  // Clean Gorilla Icon with Animation
  const GorillaIcon = () => {
    return (
      <div className="relative flex flex-col items-center">
        {/* Animated Icon Container */}
        <motion.div
          initial={{ scale: 0, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", damping: 15, delay: 0.2 }}
          className="relative"
        >
          {/* Glowing background */}
          <motion.div
            animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute inset-0 bg-blue-400 rounded-full blur-xl"
          />
          
          {/* Icon circle */}
          <div className="relative w-32 h-32 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center shadow-2xl border-4 border-white/20">
            <motion.span
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-6xl"
            >
              🦍
            </motion.span>
          </div>
          
          {/* Animated ring */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            className="absolute -inset-2 border-2 border-dashed border-blue-400/50 rounded-full"
          />
        </motion.div>
        
        {/* Speech bubble */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 10 }}
          transition={{ delay: 0.5 }}
          className="mt-4 bg-white rounded-2xl px-5 py-3 shadow-lg border border-blue-300/30 relative"
        >
          {/* Speech bubble pointer */}
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[10px] border-b-white" />
          
          <p className="text-center text-sm font-bold text-blue-600">
            🦍 You cannot enter yet!
          </p>
          <p className="text-center text-xs text-gray-500 mt-1">
            Contact Aiman: <span className="text-blue-600 font-medium">aimanalishezanbusiness@gmail.com</span>
          </p>
        </motion.div>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-blue-600"
        >
          {/* Animated background - matching landing page */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <motion.div 
              animate={{ x: [0, 80, 0], y: [0, -40, 0], opacity: [0.2, 0.3, 0.2] }} 
              transition={{ duration: 15, repeat: Infinity }} 
              className="absolute top-10 left-5 w-72 h-72 bg-blue-500/30 rounded-full blur-3xl"
            />
            <motion.div 
              animate={{ x: [0, -60, 0], y: [0, 60, 0], opacity: [0.15, 0.25, 0.15] }} 
              transition={{ duration: 12, repeat: Infinity }} 
              className="absolute bottom-10 right-5 w-56 h-56 bg-blue-700/20 rounded-full blur-3xl"
            />
            <motion.div 
              animate={{ scale: [1, 1.1, 1] }} 
              transition={{ duration: 8, repeat: Infinity }} 
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/20 rounded-full blur-3xl"
            />
          </div>
          
          {/* Main Card - 30% White */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ type: 'spring', damping: 20, stiffness: 200 }}
            className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden"
          >
            {/* Top decoration - gradient bar */}
            <div className="h-2 bg-gradient-to-r from-blue-600 via-blue-400 to-blue-600" />
            
            <div className="p-8 pt-6">
              {/* Gorilla Icon */}
              <div className="mb-6">
                <GorillaIcon />
              </div>

              {/* Title */}
              <motion.div 
                initial={{ opacity: 0, y: 10 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: 0.4 }} 
                className="text-center mb-4"
              >
                <h2 className="text-3xl font-bold text-blue-600 mb-2">{content.title}</h2>
                <p className="text-lg font-semibold text-blue-400">{content.subtitle}</p>
              </motion.div>

              <motion.p 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                transition={{ delay: 0.5 }} 
                className="text-gray-600 text-center mb-6 text-base"
              >
                {content.message}
              </motion.p>

              {/* Contact Card - Request Access */}
              <motion.div 
                initial={{ opacity: 0, y: 10 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: 0.6 }} 
                className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 mb-6 border border-blue-200"
              >
                {/* Request Access Button - mailto */}
                <motion.a 
                  href="mailto:aimanalishezanbusiness@gmail.com?subject=Access%20Request%20-%20CREATILLA&body=Hi%20Aiman,%0A%0AI%20would%20like%20to%20request%20access%20to%20CREATILLA.%0A%0AThank%20you!"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center justify-center p-4 bg-blue-600 hover:bg-blue-700 rounded-xl transition-all group mb-4 shadow-lg shadow-blue-600/30"
                >
                  <Mail className="w-5 h-5 text-white mr-3" />
                  <span className="text-white font-bold text-base">Request Access</span>
                  <ExternalLink className="w-4 h-4 text-white/70 ml-2 group-hover:text-white" />
                </motion.a>
                
                {/* Email display */}
                <div className="text-center text-sm text-blue-600 font-medium mb-3">
                  aimanalishezanbusiness@gmail.com
                </div>
                
                <div className="flex items-center justify-center space-x-2 text-blue-400">
                  <ShoppingCart className="w-4 h-4" />
                  <span className="text-sm font-medium">Or purchase our services</span>
                </div>
              </motion.div>

              {/* Return to Homepage Button */}
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={onGoToLanding}
                className="w-full bg-white hover:bg-gray-50 text-blue-600 py-4 px-6 rounded-xl font-bold text-lg border-2 border-blue-600 transition-all flex items-center justify-center space-x-2"
              >
                <Home className="w-5 h-5" />
                <span>Return to Homepage</span>
              </motion.button>
            </div>

            {/* Footer */}
            <div className="px-8 pb-6">
              <div className="flex items-center justify-center space-x-3 text-sm text-gray-400">
                <div className="w-12 h-px bg-gray-200" />
                <span className="font-medium">Powered by CREATILLA</span>
                <div className="w-12 h-px bg-gray-200" />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AccessDeniedPopup;
